import React, { FC, useRef } from 'react';
import { Box, Card, Container, Grid, Hidden, makeStyles, Theme, Typography, useMediaQuery } from '@material-ui/core';
import SilverResults from '../services/SilverResults';
import { BlueCheckIcon } from 'svgs';
import { animated } from 'react-spring';
import { useParallax } from 'hooks';

interface CarrierCardProps {
  image: string;
  title: string;
  text: string;
  staticsList: Array<{ quantity: string; text: string }>;
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    marginTop: -40,
    overflow: 'visible',
    width: '100%',
    padding: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      width: 600,
      marginTop: 110,
      padding: theme.spacing(8),
    },
  },
  box: {
    margin: theme.spacing(6, 0),
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(6, 0, 18),
    },
  },
  contentBox: {
    position: 'relative',
  },
  contentCard: {
    width: '100%',
    position: 'relative',
    zIndex: 1,
  },
  postContent: {
    display: 'flex',
    flexDirection: 'row',
  },
  contentImage: {
    order: -1,
    [theme.breakpoints.up('md')]: {
      order: 1,
    },
  },
  image: {
    width: '100%',
    transform: 'scale(1.15)',
  },
  elementBackground: {
    position: 'absolute',
    background: theme.palette.primary.main,
    bottom: -10,
    top: 'auto',
    left: -10,
    zIndex: -1,
    height: '50%',
    width: '75%',
    [theme.breakpoints.up('md')]: {
      top: -20,
      left: -20,
      width: 313,
      height: 313,
    },
  },
  icon: {
    height: 18,
    maxWidth: 18,
    marginRight: 10,
    [theme.breakpoints.up('md')]: {
      maxWidth: 22,
      height: 22,
    },
  },
  contentSilverResults: {
    marginTop: 50,
    '& div': {
      '& div': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        '& h6': {
          justifyContent: 'flex-start',
          border: 'none',
        },
        '& p': {
          marginTop: -8,
          fontSize: 16,
          width: 100,
          [theme.breakpoints.up('md')]: {
            fontSize: 18,
          },
        },
      },
    },
    [theme.breakpoints.up('md')]: {
      marginTop: 100,
    },
  },
  text: {
    fontSize: 14,
    [theme.breakpoints.up('md')]: {
      fontSize: 18,
    },
  },
  title: {
    display: 'flex',
    fontSize: 14,
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginTop: 20,
    [theme.breakpoints.up('md')]: {
      fontSize: 18,
    },
  },
}));

const CarrierCard: FC<CarrierCardProps> = ({ image, text, title, staticsList }) => {
  const classes = useStyles();
  const isMediumUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const ref = useRef(null);
  const { offset } = useParallax(ref);
  const calcCard = (o) => `translateY(${o * 0.005}px)`;
  const calcImage = (o) => `translateY(-${o * 0.005}px)`;
  const calcSquares = (o) => `translateY(${o * 0.007}px)`;

  const TextCardCarrier: FC = () => (
    <animated.div style={{ transform: offset.to(calcCard), position: 'relative' }}>
      <Card data-aos="zoom-in" data-aos-delay="100" className={classes.card}>
        <Grid className={classes.contentCard}>
          <Typography className={classes.text}>{text}</Typography>
          <Typography className={classes.title}>
            <Hidden xsDown>
              <BlueCheckIcon className={classes.icon} width="100%" viewBox="0 0 22 22" />
            </Hidden>
            {title}
          </Typography>
          <Grid className={classes.contentSilverResults}>
            {!!staticsList.length && (
              <Grid alignItems={isMediumUp ? 'center' : 'flex-start'} container>
                {staticsList.map((stat, index) => (
                  <SilverResults result={stat.quantity} description={stat.text} key={index} />
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Card>
      <Grid />
      <animated.div
        data-aos="zoom-in"
        data-aos-delay="400"
        className={classes.elementBackground}
        style={{ transform: offset.to(calcSquares) }}
      />
    </animated.div>
  );

  return (
    <div ref={ref}>
      <Box className={classes.box}>
        <Container maxWidth={false} fixed className={classes.contentBox}>
          <Grid container className={classes.postContent}>
            <Grid md={5} item className={classes.contentCard}>
              <TextCardCarrier />
            </Grid>
            <Grid md={7} item className={classes.contentImage}>
              <animated.div style={{ transform: offset.to(calcImage) }}>
                <img data-aos="zoom-in" className={classes.image} src={image} alt={image} />
              </animated.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </div>
  );
};

export default CarrierCard;
